import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { createStep, updateStep } from '../../../../store/participantService';
import { todayDate } from '../../../../utils/dateFunctions';
import { getObjFromId } from '../../../../utils/directusFunctions';
import { regExps } from '../../../../utils/formValidation/validationPatterns';
import DateSelect from '../../../formElements/DateSelect';
import SingleSelect from '../../../formElements/SingleSelect';
import TextInputField from '../../../formElements/TextInputField';
import FormActions from '../../../ui/formActions/FormActions';

import { validate } from './validateSteps';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';

const EditSteps = ({
    participant,
    onClose,
    isDisabled,
    attendedModules,
    linkedModuleId,
    formType,
    row = {}
}) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const _MESSAGE = `Participant step entry has been ${formType === 'create' ? 'added' : 'updated'}`;
    const initialState = {
        id: '',
        participantId: '',
        value: '',
        date: todayDate(),
        linkedModuleId: ''
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [initialRow, setInitialRow] = useState({});
    const [modules, setModules] = useState([]);
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // USE EFFECTS
    useEffect(() => {
        if (formType === 'create' && linkedModuleId) {
            setNewEntry({ ...initialState, linkedModuleId });
        }
        if (formType === 'edit') {
            setNewEntry(row);
            setInitialRow(row);
        }
    }, [formType, row]);

    useEffect(() => {
        let modules = [...attendedModules];
        if (formType === 'edit') {
            if (
                row.linkedModuleId &&
                row.linkedModule &&
                !modules.find((el) => el.id === row.linkedModuleId)
            ) {
                modules = [...modules, { id: row.linkedModuleId, name: row.linkedModule }];
            }
        }
        setModules(modules);
    }, [attendedModules]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onStepsChange = (e) => {
        onUpdate('value', e.target.value.replace(regExps.DIGITS_R, ''));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry, formType, initialRow);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        const payload = { ...newEntry, participantId: participant?.id };
        formType === 'create'
            ? dispatch(createStep(payload, _MESSAGE))
            : dispatch(updateStep(payload.id, payload, _MESSAGE));
        onClose();
    };

    // RENDER
    return (
        <div className={`${form.formWrapper} ${app.fadein}`}>
            <form className={form.form} onSubmit={onSubmit} data-testid="edit_step_form">
                <h2 className={app.sectionHeading}>
                    {formType === 'create' ? 'Add a New Steps Value' : 'Edit Steps Value'}
                </h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <TextInputField
                            id="value"
                            label="Step"
                            placeholder="Enter steps value..."
                            disabled={isDisabled}
                            mandatory={true}
                            maxLength={10}
                            value={newEntry.value}
                            error={errors.value}
                            onChange={onStepsChange}
                        />
                    </div>

                    <div className={form.formColumn}>
                        <DateSelect
                            value={newEntry.date}
                            label="Event Date"
                            disabled={isDisabled}
                            mandatory={true}
                            error={errors.date}
                            onDateChange={(date) => onUpdate('date', date)}
                        />
                    </div>
                </div>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="linkedModuleId"
                            label="Linked Module"
                            placeholder="Select linked module..."
                            disabled={isDisabled}
                            menuItems={modules || []}
                            selectedId={newEntry.linkedModuleId}
                            selected={getObjFromId(modules, newEntry.linkedModuleId)}
                            onChange={(chosenId) => onUpdate('linkedModuleId', chosenId)}
                        />
                    </div>
                    <div className={form.formColumn} />
                </div>

                <FormActions
                    onClose={onSubmit}
                    onCancel={onClose}
                    disabled={isButtonDisabled || isDisabled}
                    btnText={formType === 'create' ? 'Save' : 'Update'}
                    customClass="noTopBorder"
                    error={errors?.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

EditSteps.propTypes = {
    participant: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    isDisabled: PropTypes.bool,
    formType: PropTypes.string,
    row: PropTypes.object,
    attendedModules: PropTypes.arrayOf(PropTypes.object),
    linkedModuleId: PropTypes.string
};

export default EditSteps;
